// Floating Property Bar — ersetzt das rechte Inspector-Panel.
// Schwebt direkt am selektierten Element (Miro-Style), enthält nur die haeufigen
// Eigenschaften (Farbe, MoSCoW, Severity, Kind, Status, Lock, Convert, Group).
// Haupt-Text wird via Inline-Edit (Doppelklick/Enter/F2) bearbeitet, nicht hier.

import {
  type CanvasObject,
  type DependencyKind,
  type DependencyStatus,
  type Link,
  type LinkRouting,
  type LinkType,
  type Priority,
  type RequirementCategory,
  type Severity,
  type ShapeKind,
  type ShapeSemantics,
  type StickyColor,
  isCanvasObject,
  parseCanvasObject,
} from "@ms-whiteboard/shared";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type * as Y from "yjs";
import { IconPicker } from "./IconPicker.js";
import { type AlignMode, alignSelection } from "./align.js";
import { applyMindmapLayout } from "./mindmap-layout.js";
import { objectBounds } from "./object-bounds.js";
import {
  CONVERTIBLE_TYPES,
  type ConvertibleType,
  convertObject,
  isConvertibleType,
} from "./object-convert.js";
import { findFreeSlotWithAutoExpand, findRegionForHintInBlock } from "./region-routing.js";
import { BPMN_DEFAULT_KIND, BPMN_GLYPH, BPMN_SEMANTICS } from "./shape-renderer.js";
import "./FloatingPropertyBar.css";

const BAR_ASSUMED_HEIGHT = 48;
const GAP_PX = 12;
const STICKY_COLORS: readonly StickyColor[] = [
  "yellow",
  "pink",
  "mint",
  "blue",
  "lavender",
  "peach",
  "gray",
  "white",
];

const LINK_TYPES: readonly LinkType[] = ["relates-to", "resolves", "depends-on"];
const LINK_ROUTINGS: readonly LinkRouting[] = ["straight", "curved", "orthogonal"];

// Mindmap-Accent-Palette: Sticky-Style 8 Swatches + Default (Reset auf Level-Color).
// Werte sind Mindsquare-CI-konforme Pastell-Toene, die mit den Level-Defaults harmonieren.
const MINDMAP_ACCENT_PALETTE: ReadonlyArray<{ key: StickyColor; value: string }> = [
  { key: "yellow", value: "#fde68a" },
  { key: "pink", value: "#fbcfe8" },
  { key: "mint", value: "#bbf7d0" },
  { key: "blue", value: "#bfdbfe" },
  { key: "lavender", value: "#ddd6fe" },
  { key: "peach", value: "#fed7aa" },
  { key: "gray", value: "#e2e8f0" },
  { key: "white", value: "#ffffff" },
];

interface AlignAction {
  mode: AlignMode;
  i18nKey: string;
  icon: string;
}

const ALIGN_ACTIONS: readonly AlignAction[] = [
  { mode: "left", i18nKey: "align.left", icon: "⇤" },
  { mode: "center-h", i18nKey: "align.centerH", icon: "↔" },
  { mode: "right", i18nKey: "align.right", icon: "⇥" },
  { mode: "top", i18nKey: "align.top", icon: "⇡" },
  { mode: "center-v", i18nKey: "align.centerV", icon: "↕" },
  { mode: "bottom", i18nKey: "align.bottom", icon: "⇣" },
  { mode: "distribute-h", i18nKey: "align.distributeH", icon: "⇿" },
  { mode: "distribute-v", i18nKey: "align.distributeV", icon: "⇳" },
];

interface AnchorBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface FloatingPropertyBarProps {
  selectedIds: ReadonlySet<string>;
  yMap: Y.Map<unknown>;
  /** Selektion in Welt-Koordinaten — Bounding-Box-Anchor fuer die Bar-Position. */
  anchorBounds: AnchorBounds | null;
  /** World-zu-Screen-Umrechnung (von useViewport). */
  toScreen: (worldX: number, worldY: number) => { x: number; y: number };
  zoom: number;
  /** Verfuegbare Hoehe der Surface (zum Anchor-Flip oben/unten). */
  surfaceHeight: number;
  /** Wenn ein Element gerade inline editiert wird, blendet sich die Bar aus. */
  editingId: string | null;
  selectedLink?: Link | null;
  onUpdateLinkType?: (id: string, type: LinkType) => void;
  onUpdateLinkRouting?: (id: string, routing: LinkRouting) => void;
  onDeleteLink?: (id: string) => void;
}

export function FloatingPropertyBar({
  selectedIds,
  yMap,
  anchorBounds,
  toScreen,
  zoom,
  surfaceHeight,
  editingId,
  selectedLink,
  onUpdateLinkType,
  onUpdateLinkRouting,
  onDeleteLink,
}: FloatingPropertyBarProps): JSX.Element | null {
  if (selectedLink && onUpdateLinkType && onUpdateLinkRouting && onDeleteLink) {
    const pos = computePositionForRect(anchorBounds, toScreen, zoom, surfaceHeight);
    if (!pos) return null;
    return (
      <div className="floating-bar" style={{ left: pos.left, top: pos.top }} role="toolbar">
        <LinkBar
          link={selectedLink}
          onUpdate={onUpdateLinkType}
          onUpdateRouting={onUpdateLinkRouting}
          onDelete={onDeleteLink}
        />
      </div>
    );
  }

  if (selectedIds.size === 0 || !anchorBounds) return null;

  const pos = computePositionForRect(anchorBounds, toScreen, zoom, surfaceHeight);
  if (!pos) return null;

  if (selectedIds.size > 1) {
    return (
      <div className="floating-bar" style={{ left: pos.left, top: pos.top }} role="toolbar">
        <MultiSelectBar selectedIds={selectedIds} yMap={yMap} />
      </div>
    );
  }

  const [id] = selectedIds;
  if (!id) return null;
  // Während Inline-Edit verstecken — die Bar wuerde sonst den Cursor verdecken.
  if (editingId === id) return null;
  return (
    <div className="floating-bar" style={{ left: pos.left, top: pos.top }} role="toolbar">
      <SingleBar id={id} yMap={yMap} />
    </div>
  );
}

function computePositionForRect(
  anchor: AnchorBounds | null,
  toScreen: (x: number, y: number) => { x: number; y: number },
  zoom: number,
  surfaceHeight: number,
): { left: number; top: number } | null {
  if (!anchor) return null;
  const topLeft = toScreen(anchor.x, anchor.y);
  const screenW = anchor.width * zoom;
  const screenH = anchor.height * zoom;
  const centerX = topLeft.x + screenW / 2;
  const wantAbove = topLeft.y - GAP_PX - BAR_ASSUMED_HEIGHT;
  // Wenn oben zu wenig Platz → Bar unter dem Element.
  const placeAbove = wantAbove >= 8;
  const top = placeAbove ? wantAbove : topLeft.y + screenH + GAP_PX;
  // Wenn der Anker komplett ausserhalb der sichtbaren Surface ist, Bar nicht zeigen.
  if (top < -BAR_ASSUMED_HEIGHT || top > surfaceHeight) return null;
  return { left: centerX, top };
}

function MultiSelectBar({
  selectedIds,
  yMap,
}: {
  selectedIds: ReadonlySet<string>;
  yMap: Y.Map<unknown>;
}): JSX.Element {
  const { t } = useTranslation();
  let sharedGroupId: string | null | undefined = undefined;
  let allGrouped = true;
  for (const id of selectedIds) {
    const raw = yMap.get(id);
    if (!isCanvasObject(raw)) continue;
    const gid = raw.groupId ?? null;
    if (sharedGroupId === undefined) sharedGroupId = gid;
    else if (sharedGroupId !== gid) {
      allGrouped = false;
      break;
    }
    if (gid === null) allGrouped = false;
  }
  const isGrouped = allGrouped && sharedGroupId !== null && sharedGroupId !== undefined;

  const group = (): void => {
    const groupId = crypto.randomUUID();
    yMap.doc?.transact(() => {
      for (const id of selectedIds) {
        const raw = yMap.get(id);
        if (!isCanvasObject(raw)) continue;
        yMap.set(id, { ...raw, groupId });
      }
    });
  };
  const ungroup = (): void => {
    yMap.doc?.transact(() => {
      for (const id of selectedIds) {
        const raw = yMap.get(id);
        if (!isCanvasObject(raw)) continue;
        const { groupId: _g, ...rest } = raw as CanvasObject & { groupId?: string };
        yMap.set(id, rest as CanvasObject);
      }
    });
  };

  return (
    <>
      <span className="floating-bar-count" title={t("inspector.multiSelect.title")}>
        {selectedIds.size}
      </span>
      <div className="floating-bar-align-row" aria-label={t("align.bar.title")}>
        {ALIGN_ACTIONS.map((a, i) => (
          <button
            key={a.mode}
            type="button"
            className={`floating-bar-icon-btn ${
              i === 2 || i === 5 ? "floating-bar-align-divider-after" : ""
            }`}
            onClick={() => alignSelection(yMap, selectedIds, a.mode)}
            title={t(a.i18nKey)}
            aria-label={t(a.i18nKey)}
          >
            {a.icon}
          </button>
        ))}
      </div>
      {isGrouped ? (
        <button type="button" className="floating-bar-btn" onClick={ungroup}>
          {t("inspector.group.ungroup")}
        </button>
      ) : (
        <button type="button" className="floating-bar-btn" onClick={group}>
          {t("inspector.group.group")}
        </button>
      )}
    </>
  );
}

function LinkBar({
  link,
  onUpdate,
  onUpdateRouting,
  onDelete,
}: {
  link: Link;
  onUpdate: (id: string, type: LinkType) => void;
  onUpdateRouting: (id: string, routing: LinkRouting) => void;
  onDelete: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <span className="floating-bar-label">{t("inspector.link.title")}</span>
      <PillRow
        value={link.type}
        options={LINK_TYPES.map((lt) => ({
          value: lt,
          label: t(`inspector.linkType.${lt}`),
        }))}
        onChange={(v) => onUpdate(link.id, v as LinkType)}
      />
      <PillRow
        value={link.routing ?? "straight"}
        options={LINK_ROUTINGS.map((r) => ({
          value: r,
          label: t(`link.routing.${r}`),
        }))}
        onChange={(v) => onUpdateRouting(link.id, v as LinkRouting)}
      />
      <button
        type="button"
        className="floating-bar-btn floating-bar-danger"
        onClick={() => onDelete(link.id)}
        title={t("inspector.link.delete")}
        aria-label={t("inspector.link.delete")}
      >
        ✕
      </button>
    </>
  );
}

function SingleBar({ id, yMap }: { id: string; yMap: Y.Map<unknown> }): JSX.Element | null {
  const { t } = useTranslation();
  const [obj, setObj] = useState<CanvasObject | null>(() => {
    const raw = yMap.get(id);
    return isCanvasObject(raw) ? raw : null;
  });
  const [iconPickerOpen, setIconPickerOpen] = useState(false);

  useEffect(() => {
    const update = (): void => {
      const raw = yMap.get(id);
      setObj(isCanvasObject(raw) ? raw : null);
    };
    update();
    yMap.observe(update);
    return () => yMap.unobserve(update);
  }, [yMap, id]);

  if (!obj) return null;

  const commit = (patch: Partial<CanvasObject>): void => {
    const next = { ...obj, ...patch } as unknown;
    const parsed = parseCanvasObject(next);
    if (!parsed) return;
    yMap.set(id, parsed);
  };

  // BPMN-Semantik wechseln: setzt zugleich den passenden Default-Kind; "keine" entfernt
  // das Feld (Zod stripped undefined optionals).
  const commitSemantics = (next: ShapeSemantics | undefined): void => {
    if (next === undefined) {
      commit({ semantics: undefined } as Partial<CanvasObject>);
      return;
    }
    commit({ kind: BPMN_DEFAULT_KIND[next], semantics: next } as Partial<CanvasObject>);
  };

  const convert = (target: ConvertibleType): void => {
    if (!isConvertibleType(obj.type)) return;
    const next = convertObject(obj, target, t("inspector.field.text.placeholder"));
    if (!next) return;
    yMap.set(id, next);
  };

  const leaveGroup = (): void => {
    const { groupId: _g, ...rest } = obj as CanvasObject & { groupId?: string };
    yMap.doc?.transact(() => yMap.set(id, rest as CanvasObject));
  };

  const relayoutMindmap = (): void => {
    if (obj.type !== "mindmap-node") return;
    applyMindmapLayout(yMap, obj.id);
  };

  // Generischer Property-Wechsel mit Auto-Route: wenn das Objekt aktuell in einer Region
  // mit semanticHint[hintField] sitzt UND der neue Wert anders ist, springe in den
  // passenden Geschwister-Quadranten desselben Method-Blocks. Sonst nur committen.
  // Funktioniert fuer priority (MoSCoW), severity (pain-dependency), category (req-cluster),
  // status (potenziell) — jeder Hint-Key der vom Scaffold gesetzt wird.
  const routeOnPropertyChange = (hintField: string, next: string): void => {
    const patch = { [hintField]: next } as Partial<CanvasObject>;
    if (!obj.parentRegionId) {
      commit(patch);
      return;
    }
    const currentRegion = yMap.get(obj.parentRegionId);
    if (!(isCanvasObject(currentRegion) && currentRegion.type === "region")) {
      commit(patch);
      return;
    }
    const oldHint = currentRegion.semanticHint?.[hintField];
    if (!oldHint || oldHint === next) {
      commit(patch);
      return;
    }
    // acceptType = obj.type: nur in Regionen routen, die diesen Typ akzeptieren —
    // sonst springt z.B. eine Dependency bei Severity-Wechsel in die Pain-Spalte.
    const target = findRegionForHintInBlock(
      yMap,
      currentRegion.blockId,
      hintField,
      next,
      obj.type,
    );
    if (!target || target.id === currentRegion.id) {
      commit(patch);
      return;
    }
    yMap.doc?.transact(() => {
      const b = objectBounds(obj);
      const slot = findFreeSlotWithAutoExpand(yMap, target, { width: b.w, height: b.h }, obj.id);
      const patched = {
        ...obj,
        x: slot.x,
        y: slot.y,
        parentRegionId: target.id,
        [hintField]: next,
      };
      const parsed = parseCanvasObject(patched);
      if (parsed) yMap.set(id, parsed);
    });
  };

  const commitPriority = (next: Priority): void => routeOnPropertyChange("priority", next);
  const commitSeverity = (next: Severity): void => routeOnPropertyChange("severity", next);
  const commitCategory = (next: RequirementCategory): void =>
    routeOnPropertyChange("category", next);
  const commitStatus = (next: DependencyStatus): void => routeOnPropertyChange("status", next);

  return (
    <>
      <span className="floating-bar-type">{t(`inspector.type.${obj.type}`)}</span>

      {obj.type === "sticky" && (
        <div
          className="floating-bar-swatches"
          role="radiogroup"
          aria-label={t("inspector.section.color")}
        >
          {STICKY_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={(obj.color ?? "yellow") === c}
              title={t(`inspector.color.${c}`)}
              aria-label={t(`inspector.color.${c}`)}
              className={`floating-bar-swatch is-${c} ${
                (obj.color ?? "yellow") === c ? "is-active" : ""
              }`}
              onClick={() => commit({ color: c } as Partial<CanvasObject>)}
            />
          ))}
        </div>
      )}

      {obj.type === "mindmap-node" && (
        <div
          className="floating-bar-swatches"
          role="radiogroup"
          aria-label={t("inspector.section.color")}
        >
          <button
            type="button"
            role="radio"
            aria-checked={!obj.accent}
            title={t("inspector.color.default")}
            aria-label={t("inspector.color.default")}
            className={`floating-bar-swatch is-default ${!obj.accent ? "is-active" : ""}`}
            onClick={() => commit({ accent: undefined } as Partial<CanvasObject>)}
          />
          {MINDMAP_ACCENT_PALETTE.map((c) => (
            <button
              key={c.value}
              type="button"
              role="radio"
              aria-checked={obj.accent === c.value}
              title={t(`inspector.color.${c.key}`)}
              aria-label={t(`inspector.color.${c.key}`)}
              className={`floating-bar-swatch ${obj.accent === c.value ? "is-active" : ""}`}
              style={{ background: c.value }}
              onClick={() => commit({ accent: c.value } as Partial<CanvasObject>)}
            />
          ))}
        </div>
      )}

      {obj.type === "icon" && (
        <div className="floating-bar-icon-anchor">
          <button
            type="button"
            className="floating-bar-btn"
            onClick={() => setIconPickerOpen((p) => !p)}
            title={t("inspector.icon.changeIcon")}
            aria-haspopup="menu"
            aria-expanded={iconPickerOpen}
          >
            {t("inspector.icon.changeIcon")} ▾
          </button>
          {iconPickerOpen && (
            <div className="floating-bar-icon-flyout" role="menu">
              <IconPicker
                value={obj.iconName}
                onChange={(name) => {
                  commit({ iconName: name } as Partial<CanvasObject>);
                  setIconPickerOpen(false);
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Icon-Farbpalette ausgeblendet (Welle-3-Findings): Pastell-Toene zu blass auf
          Lucide-Strokes. Wartet auf eine eigene Icon-taugliche Palette (Backlog). */}

      {obj.type === "shape" && (
        <div
          className="floating-bar-pillrow"
          role="radiogroup"
          aria-label={t("inspector.shape.kind")}
        >
          {(
            ["rounded-rect", "ellipse", "circle", "diamond", "hexagon", "parallelogram"] as const
          ).map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={obj.kind === k}
              className={`floating-bar-pill ${obj.kind === k ? "is-active" : ""}`}
              onClick={() => commit({ kind: k satisfies ShapeKind } as Partial<CanvasObject>)}
              title={t(`inspector.shape.kind.${k}`)}
            >
              {k === "rounded-rect"
                ? "▢"
                : k === "ellipse"
                  ? "◯"
                  : k === "circle"
                    ? "⬤"
                    : k === "diamond"
                      ? "◇"
                      : k === "hexagon"
                        ? "⬡"
                        : "▱"}
            </button>
          ))}
        </div>
      )}

      {obj.type === "shape" && (
        <div
          className="floating-bar-pillrow"
          role="radiogroup"
          aria-label={t("canvas.tool.shape.bpmn")}
        >
          <button
            type="button"
            role="radio"
            aria-checked={!obj.semantics}
            className={`floating-bar-pill ${!obj.semantics ? "is-active" : ""}`}
            onClick={() => commitSemantics(undefined)}
            title={t("shape.semantics.none")}
          >
            —
          </button>
          {BPMN_SEMANTICS.map((sem) => (
            <button
              key={sem}
              type="button"
              role="radio"
              aria-checked={obj.semantics === sem}
              className={`floating-bar-pill ${obj.semantics === sem ? "is-active" : ""}`}
              onClick={() => commitSemantics(sem)}
              title={t(`shape.semantics.${sem.replace("bpmn:", "")}`)}
            >
              {BPMN_GLYPH[sem]}
            </button>
          ))}
        </div>
      )}

      {obj.type === "requirement" && (
        <>
          <PillRow
            label={t("inspector.field.priority")}
            value={obj.priority}
            options={[
              { value: "must", label: t("inspector.priority.must") },
              { value: "should", label: t("inspector.priority.should") },
              { value: "could", label: t("inspector.priority.could") },
              { value: "wont", label: t("inspector.priority.wont") },
            ]}
            onChange={(v) => commitPriority(v as Priority)}
          />
          <PillRow
            label={t("inspector.field.category")}
            value={obj.category}
            options={[
              { value: "functional", label: t("inspector.reqCategory.functional") },
              { value: "non-functional", label: t("inspector.reqCategory.nonFunctional") },
              { value: "data", label: t("inspector.reqCategory.data") },
              { value: "out-of-scope", label: t("inspector.reqCategory.outOfScope") },
            ]}
            onChange={(v) => commitCategory(v as RequirementCategory)}
          />
        </>
      )}

      {obj.type === "pain" && (
        <PillRow
          label={t("inspector.field.severity")}
          value={obj.severity}
          options={[
            { value: "low", label: t("inspector.severity.low") },
            { value: "med", label: t("inspector.severity.med") },
            { value: "high", label: t("inspector.severity.high") },
          ]}
          onChange={(v) => commitSeverity(v as Severity)}
        />
      )}

      {obj.type === "dependency" && (
        <>
          <PillRow
            label={t("inspector.field.kind")}
            value={obj.kind}
            options={[
              { value: "internal", label: t("inspector.depKind.internal") },
              { value: "external", label: t("inspector.depKind.external") },
              { value: "org", label: t("inspector.depKind.org") },
            ]}
            onChange={(v) => commit({ kind: v as DependencyKind } as Partial<CanvasObject>)}
          />
          <PillRow
            label={t("inspector.field.status")}
            value={obj.status}
            options={[
              { value: "open", label: t("inspector.depStatus.open") },
              { value: "confirmed", label: t("inspector.depStatus.confirmed") },
              { value: "at-risk", label: t("inspector.depStatus.atRisk") },
            ]}
            onChange={(v) => commitStatus(v as DependencyStatus)}
          />
          <PillRow
            label={t("inspector.field.severity")}
            value={obj.severity ?? ""}
            options={[
              { value: "low", label: t("inspector.severity.low") },
              { value: "med", label: t("inspector.severity.med") },
              { value: "high", label: t("inspector.severity.high") },
            ]}
            onChange={(v) => commitSeverity(v as Severity)}
          />
        </>
      )}

      {isConvertibleType(obj.type) && (
        <ConvertMenu currentType={obj.type as ConvertibleType} onConvert={convert} />
      )}

      {obj.type === "mindmap-node" && (
        <button
          type="button"
          className="floating-bar-btn"
          onClick={relayoutMindmap}
          title={t("mindmap.relayout")}
        >
          {t("mindmap.relayout")}
        </button>
      )}

      <button
        type="button"
        className={`floating-bar-icon-btn ${obj.locked ? "is-on" : ""}`}
        title={t("inspector.field.locked")}
        aria-label={t("inspector.field.locked")}
        aria-pressed={obj.locked === true}
        onClick={() => commit({ locked: !obj.locked } as Partial<CanvasObject>)}
      >
        {obj.locked ? "🔒" : "🔓"}
      </button>

      {obj.groupId && (
        <button
          type="button"
          className="floating-bar-btn"
          onClick={leaveGroup}
          title={t("inspector.group.leave")}
        >
          {t("inspector.group.leave")}
        </button>
      )}
    </>
  );
}

interface PillOption {
  value: string;
  label: string;
}

function PillRow({
  label,
  value,
  options,
  onChange,
}: {
  label?: string;
  value: string;
  options: readonly PillOption[];
  onChange: (next: string) => void;
}): JSX.Element {
  return (
    <div className="floating-bar-pills" role="radiogroup" aria-label={label}>
      {label && <span className="floating-bar-pill-label">{label}</span>}
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={value === opt.value}
          className={`floating-bar-pill ${value === opt.value ? "is-active" : ""}`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function ConvertMenu({
  currentType,
  onConvert,
}: {
  currentType: ConvertibleType;
  onConvert: (target: ConvertibleType) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const targets = CONVERTIBLE_TYPES.filter((t2) => t2 !== currentType);
  return (
    <div className="floating-bar-convert">
      <button
        type="button"
        className="floating-bar-btn"
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {t("inspector.section.convert")} ▾
      </button>
      {open && (
        <div className="floating-bar-convert-menu" role="menu">
          {targets.map((target) => (
            <button
              key={target}
              type="button"
              role="menuitem"
              className="floating-bar-convert-item"
              onClick={() => {
                onConvert(target);
                setOpen(false);
              }}
            >
              → {t(`inspector.type.${target}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
